// ─── Admin Chrome State Hook ──────────────────────────────────────────────────
//
// Derives impersonation / view-as state for admin personas. School context
// lives in useSchoolContext so staff routes do not depend on header chrome.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback } from 'react';
import type { ShellPersona } from '../app-shell/moduleRegistry';
import {
  useClearImpersonationSessionMutation,
  useClearViewAsContextMutation,
  useGetImpersonationSessionQuery,
  useGetViewAsContextQuery,
  useLaunchImpersonationSessionMutation,
} from './domainRouterApi';
import { useShellProfile } from './useShellProfile';
import { useSchoolContext, type TenantSchool } from './useSchoolContext';

// ─── Public surface types ─────────────────────────────────────────────────────

export type SuperAdminTargetPersona = 'administrator' | 'staff' | 'parent';

export type ImpersonationSession = {
  sessionId: string;
  actorId: string;
  actorEmail: string;
  effectiveUserId: string;
  effectiveUserName: string;
  effectiveUserEmail: string;
  targetPersona?: SuperAdminTargetPersona | string;
  startedAt: string;
  reason?: string;
};

export type LaunchImpersonationSessionInput = {
  effectiveUserId?: string;
  effectiveUserName?: string;
  effectiveUserEmail?: string;
  targetPersona?: SuperAdminTargetPersona;
  reason: string;
};

export type ViewAsContext = {
  userId: string;
  userName: string;
  userEmail: string;
};

export type { TenantSchool } from './useSchoolContext';
export { shouldRenderHeaderAdminChrome } from './adminChromeVisibility';

export type AdminChromeState = {
  /** True when the effective persona is 'administrator' or 'super-administrator'. */
  isAdminChrome: boolean;
  /** True when the effective persona is exactly 'super-administrator'. */
  isSuperAdmin: boolean;
  effectivePersona: ShellPersona;
  /** Display name resolved from Cognito profile; falls back to email prefix. */
  displayName: string;
  /** Primary email from Cognito profile; empty string when not authenticated. */
  displayEmail: string;
  // ── Backend stubs — null until AppSync connectors are wired ─────────────────
  /** TODO(AppSync expert): wire useGetImpersonationSessionQuery */
  impersonationSession: ImpersonationSession | null;
  /** TODO(AppSync expert): wire useGetViewAsContextQuery */
  viewAsContext: ViewAsContext | null;
  tenantSchools: TenantSchool[] | null;
  activeSchoolId: string | null;
  isLoadingContext: boolean;
  impersonationError: string | null;
  isLaunchingImpersonation: boolean;
  launchImpersonationSession: (
    input: LaunchImpersonationSessionInput,
  ) => Promise<void>;
  clearImpersonationSession: () => Promise<void>;
  clearViewAsContext: () => Promise<void>;
  switchSchoolContext: (schoolId: string) => Promise<void>;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAdminChrome(): AdminChromeState {
  const { profile, effectivePersona, basePersona } = useShellProfile();
  const adminAuthorityPersona =
    basePersona === 'administrator' || basePersona === 'super-administrator'
      ? basePersona
      : effectivePersona;

  const isAdminChrome =
    adminAuthorityPersona === 'administrator' ||
    adminAuthorityPersona === 'super-administrator';
  const isSuperAdmin = adminAuthorityPersona === 'super-administrator';
  const impersonationQueryOptions = { skip: !isSuperAdmin };
  const viewAsQueryOptions = { skip: !isAdminChrome };
  const impersonationQuery = useGetImpersonationSessionQuery(
    undefined,
    impersonationQueryOptions,
  );
  const viewAsQuery = useGetViewAsContextQuery(undefined, viewAsQueryOptions);
  const schoolContext = useSchoolContext();
  const [launchImpersonationMutation, launchImpersonationState] =
    useLaunchImpersonationSessionMutation();
  const [clearImpersonationMutation, clearImpersonationState] =
    useClearImpersonationSessionMutation();
  const [clearViewAsMutation, clearViewAsState] =
    useClearViewAsContextMutation();

  const rawEmail = profile?.email?.trim() ?? '';
  const displayEmail = rawEmail;

  // Derive a human-readable display name from the email prefix when no explicit
  // name attribute is available from Cognito. This avoids surfacing an empty
  // string in the admin context chip.
  const displayName = rawEmail.includes('@')
    ? rawEmail.split('@')[0]
    : rawEmail || 'Admin';
  const launchImpersonationSession = useCallback(
    async (input: LaunchImpersonationSessionInput) => {
      await launchImpersonationMutation(input).unwrap();
    },
    [launchImpersonationMutation],
  );
  const clearImpersonationSession = useCallback(async () => {
    await clearImpersonationMutation().unwrap();
  }, [clearImpersonationMutation]);
  const clearViewAsContext = useCallback(async () => {
    await clearViewAsMutation().unwrap();
  }, [clearViewAsMutation]);
  const mutationError =
    launchImpersonationState.error ??
    clearImpersonationState.error ??
    clearViewAsState.error ??
    null;
  const impersonationError =
    mutationError &&
    typeof mutationError === 'object' &&
    'message' in mutationError
      ? String(
          (mutationError as { message?: unknown }).message ??
            'Admin governance operation failed.',
        )
      : mutationError
        ? 'Admin governance operation failed.'
        : null;
  return {
    isAdminChrome,
    isSuperAdmin,
    effectivePersona,
    displayName,
    displayEmail,
    impersonationSession: impersonationQuery.data ?? null,
    viewAsContext: viewAsQuery.data ?? null,
    tenantSchools: schoolContext.schools,
    activeSchoolId: schoolContext.activeSchoolId,
    isLoadingContext:
      impersonationQuery.isLoading ||
      viewAsQuery.isLoading ||
      schoolContext.isLoading,
    impersonationError,
    isLaunchingImpersonation: launchImpersonationState.isLoading,
    launchImpersonationSession,
    clearImpersonationSession,
    clearViewAsContext,
    switchSchoolContext: schoolContext.selectSchool,
  };
}
